import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, IconButton, InputAdornment, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Add, AssessmentOutlined, Clear, Delete, Drafts, Edit, EditNoteOutlined, MoreVert, Publish, Search } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import { examService } from '../../services/examService';
import { examTypeService } from '../../services/examTypeService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useSessionContext } from '../../context/SessionContext';
import type { ExamGroupDetail, ExamGroupRow, ExamType, MarkingProgress } from '../../types';
import type { Session, SessionClass } from '../../types/sessions';
import ConfirmationDialog, { ConfirmFacts } from '../../components/common/ConfirmationDialog';
import FilterBar from '../../components/common/FilterBar';
import SearchableSelect from '../../components/common/SearchableSelect';
import EmptyState from '../../components/common/EmptyState';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import { ToneChip } from '../../components/common/StatusChip';
import { tableHeadCellSx } from '../../components/admissions/tabLayout';
import { buildSessionOptions } from '../../utils/sessionOptions';
import { fmtClassName } from '../../utils/pdf';
import ExamFormModal from '../../components/examination/ExamFormModal';
import ExamDetailDrawer from '../../components/examination/ExamDetailDrawer';
import { markedExamBlock, progressText } from '../../components/examination/examGroupGuards';

/** What the publish / unpublish / delete confirmations need to know about an exam. */
interface ExamRef {
  examGroupId: string;
  name: string;
  classes: Array<{ _id: string; name: string }>;
  progress: MarkingProgress;
}

const STATUS_OPTIONS = [{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }];

const dateRange = (a: string, b: string) => {
  const s = new Date(a); const e = new Date(b);
  return format(s, 'd MMM yyyy') === format(e, 'd MMM yyyy') ? format(s, 'd MMM yyyy') : `${format(s, 'd MMM')} – ${format(e, 'd MMM yyyy')}`;
};

const refOf = (g: ExamGroupRow | ExamGroupDetail): ExamRef => ({
  examGroupId: g.examGroupId,
  name: g.name,
  classes: 'classes' in g ? g.classes : g.perClass.map(p => p.class),
  progress: g.progress,
});

/**
 * Manage Exams — each exam across all its classes, with how far its marking
 * has got. Drafts can change freely; a published exam can still be
 * corrected (the form says what its recorded marks allow). An exam with
 * marks can't be unpublished or deleted until they are cleared.
 */
const ManageExamsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { activeSession, loading: sessionLoading } = useSessionContext();
  const canManage = canWrite('create_exam');

  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [classId, setClassId] = useState('');
  const [examTypeId, setExamTypeId] = useState('');
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [groups, setGroups] = useState<ExamGroupRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ExamGroupDetail | null>(null);
  const [drawerGroupId, setDrawerGroupId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; group: ExamGroupRow } | null>(null);
  const [confirm, setConfirm] = useState<{ action: 'publish' | 'unpublish' | 'delete'; exam: ExamRef } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([examTypeService.getExamTypes({ limit: 100 }), sessionsApi.list(), classesApi.list()])
      .then(([t, s, c]) => { setExamTypes(t.data?.examTypes ?? []); setSessions(s); setClasses(c); })
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the filters', { variant: 'error' }));
  }, [enqueueSnackbar]);

  // Start on the active session, so the first list isn't every session's exams.
  useEffect(() => {
    if (sessionLoading || ready) return;
    if (activeSession?._id) setSessionId(activeSession._id);
    setReady(true);
  }, [sessionLoading, activeSession, ready]);

  useEffect(() => { setPage(0); }, [sessionId, classId, examTypeId, status, search]);

  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!ready) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await examService.getExamGroups({
        page: page + 1,
        limit: rowsPerPage,
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        examTypeId: examTypeId || undefined,
        status: (status || undefined) as 'draft' | 'published' | undefined,
        search: search.trim() || undefined,
      });
      if (mine !== seq.current) return;
      setGroups(res.data?.groups ?? []);
      setTotal(res.data?.pagination?.totalItems ?? 0);
    } catch (err: any) {
      if (mine === seq.current) enqueueSnackbar(err?.message || 'Could not load the exams', { variant: 'error' });
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [ready, page, rowsPerPage, sessionId, classId, examTypeId, status, search, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const sessionOptions = useMemo(() => buildSessionOptions(sessions, theme), [sessions, theme]);
  const classOptions = useMemo(() => classes.map(c => ({ value: c._id, label: fmtClassName(c.name) })), [classes]);
  const typeOptions = useMemo(() => examTypes.map(t => ({ value: t._id, label: t.name })), [examTypes]);
  const filtered = !!(classId || examTypeId || status || search.trim());

  const openEdit = async (examGroupId: string) => {
    setBusy(examGroupId);
    try {
      const res = await examService.getExamGroup(examGroupId);
      setEditingGroup(res.data ?? null);
      setModalOpen(true);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load the exam', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    const { action, exam } = confirm;
    setBusy(exam.examGroupId);
    try {
      if (action === 'publish') {
        const res = await examService.publishExamGroup(exam.examGroupId);
        enqueueSnackbar(res.message || `${exam.name} published`, {
          variant: 'success',
          action: () => (
            <Button color="inherit" size="small" onClick={() => navigate(`/app/examination/marks?examGroupId=${exam.examGroupId}`)}>Enter marks</Button>
          ),
        });
      } else if (action === 'unpublish') {
        await examService.unpublishExamGroup(exam.examGroupId);
        enqueueSnackbar(`${exam.name} is a draft again`, { variant: 'success' });
      } else {
        await examService.deleteExamGroup(exam.examGroupId);
        enqueueSnackbar(`${exam.name} deleted`, { variant: 'success' });
      }
      setConfirm(null);
      load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'That didn\'t go through', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const fromDrawer = (action: 'publish' | 'unpublish' | 'delete') => (g: ExamGroupDetail) => {
    setDrawerGroupId(null);
    setConfirm({ action, exam: refOf(g) });
  };

  const confirmCopy = confirm && {
    publish: {
      title: `Publish ${confirm.exam.name}?`,
      message: 'Every student now in these classes gets the exam to be marked. Students who join later get it automatically. You can still correct the exam afterwards.',
      confirmText: 'Publish',
      color: 'success' as const,
    },
    unpublish: {
      title: `Unpublish ${confirm.exam.name}?`,
      message: 'It goes back to draft and leaves Enter Marks. Nothing has been marked yet, so nothing is lost.',
      confirmText: 'Unpublish',
      color: 'warning' as const,
    },
    delete: {
      title: `Delete ${confirm.exam.name}?`,
      message: 'The exam is removed from every class. Nothing has been marked, so no marks are lost.',
      confirmText: 'Delete',
      color: 'error' as const,
    },
  }[confirm.action];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Manage Exams</Typography>
          <Typography variant="body2" color="text.secondary">Create an exam for one or more classes, publish it, and follow its marking.</Typography>
        </Box>
        {canManage && (
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => { setEditingGroup(null); setModalOpen(true); }}>
            Create exam
          </Button>
        )}
      </Stack>

      <FilterBar loading={loading && groups.length > 0}>
        <TextField
          size="small"
          placeholder="Search exams"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 300 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
            endAdornment: searchInput ? <InputAdornment position="end"><IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton></InputAdornment> : undefined,
          }}
        />
        <SearchableSelect label="Session" placeholder="All sessions" value={sessionId} onChange={setSessionId} options={sessionOptions} minWidth={200} />
        <SearchableSelect label="Class" placeholder="All classes" value={classId} onChange={setClassId} options={classOptions} minWidth={150} />
        <SearchableSelect label="Exam type" placeholder="All types" value={examTypeId} onChange={setExamTypeId} options={typeOptions} minWidth={160} />
        <SearchableSelect label="Status" placeholder="Any" value={status} onChange={setStatus} options={STATUS_OPTIONS} minWidth={140} />
      </FilterBar>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': tableHeadCellSx }}>
                <TableCell>Exam</TableCell>
                <TableCell>Classes</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Marking</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" width={96} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && groups.length === 0 ? (
                <TableRowSkeletons columns={6} rows={6} />
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<AssessmentOutlined />}
                      title={filtered ? 'No exams match' : 'No exams yet'}
                      description={filtered ? 'Try another class, type or status.' : 'Create an exam, choose its classes and subjects, then publish it for marking.'}
                      action={!filtered && canManage ? { label: 'Create exam', onClick: () => { setEditingGroup(null); setModalOpen(true); }, icon: <Add /> } : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : groups.map(g => {
                const published = g.status === 'published';
                const pct = g.progress.papers ? (g.progress.recorded / g.progress.papers) * 100 : 0;
                return (
                  <TableRow key={g.examGroupId} hover onClick={() => setDrawerGroupId(g.examGroupId)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{g.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {g.examTypeId?.name ?? '—'}{g.sessionId ? ` · ${g.sessionId.name}` : ''}{g.countsInReports ? '' : ' · not in reports'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={g.classes.length > 3 ? g.classes.map(c => fmtClassName(c.name)).join(', ') : ''}>
                        <Typography variant="body2">
                          {g.classes.slice(0, 3).map(c => fmtClassName(c.name)).join(', ')}{g.classes.length > 3 ? ` +${g.classes.length - 3}` : ''}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{dateRange(g.startDate, g.endDate)}</Typography></TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      {published ? (
                        <>
                          <LinearProgress variant="determinate" value={pct} color={g.progress.papers > 0 && g.progress.locked === g.progress.papers ? 'success' : 'primary'} sx={{ height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.06) }} />
                          <Typography variant="caption" color="text.secondary">{progressText(g.progress)}</Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Publish to start marking</Typography>
                      )}
                    </TableCell>
                    <TableCell><ToneChip label={published ? 'Published' : 'Draft'} tone={published ? 'success' : 'pending'} /></TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                      {canManage && (
                        <>
                          <Tooltip title="Edit">
                            <span>
                              <IconButton size="small" onClick={() => openEdit(g.examGroupId)} disabled={busy === g.examGroupId} aria-label={`Edit ${g.name}`}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton size="small" onClick={e => setMenu({ anchor: e.currentTarget, group: g })} aria-label={`More for ${g.name}`}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)} PaperProps={{ sx: { minWidth: 200 } }}>
        {menu && (menu.group.status === 'draft' ? (
          <MenuItem dense onClick={() => { setConfirm({ action: 'publish', exam: refOf(menu.group) }); setMenu(null); }}>
            <ListItemIcon><Publish fontSize="small" sx={{ color: 'success.main' }} /></ListItemIcon>
            <ListItemText>Publish</ListItemText>
          </MenuItem>
        ) : [
          <MenuItem key="marks" dense onClick={() => navigate(`/app/examination/marks?examGroupId=${menu.group.examGroupId}`)}>
            <ListItemIcon><EditNoteOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Enter marks</ListItemText>
          </MenuItem>,
          <Tooltip key="unpublish" title={markedExamBlock(menu.group.progress, 'unpublish')} placement="left">
            <span>
              <MenuItem dense disabled={!!markedExamBlock(menu.group.progress, 'unpublish')} onClick={() => { setConfirm({ action: 'unpublish', exam: refOf(menu.group) }); setMenu(null); }}>
                <ListItemIcon><Drafts fontSize="small" sx={{ color: 'warning.main' }} /></ListItemIcon>
                <ListItemText>Unpublish</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>,
        ])}
        {menu && (
          <Tooltip title={markedExamBlock(menu.group.progress, 'delete')} placement="left">
            <span>
              <MenuItem dense disabled={!!markedExamBlock(menu.group.progress, 'delete')} onClick={() => { setConfirm({ action: 'delete', exam: refOf(menu.group) }); setMenu(null); }}>
                <ListItemIcon><Delete fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ color: 'error.main' }}>Delete</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>

      <ExamFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingGroup(null); }}
        onSaved={load}
        editingGroup={editingGroup}
        sessions={sessions}
        classes={classes}
        examTypes={examTypes}
        activeSession={activeSession}
      />

      <ExamDetailDrawer
        open={!!drawerGroupId}
        examGroupId={drawerGroupId}
        canManage={canManage}
        onClose={() => setDrawerGroupId(null)}
        onEdit={g => { setDrawerGroupId(null); setEditingGroup(g); setModalOpen(true); }}
        onPublish={fromDrawer('publish')}
        onUnpublish={fromDrawer('unpublish')}
        onDelete={fromDrawer('delete')}
      />

      {confirm && confirmCopy && (
        <ConfirmationDialog
          open
          onClose={() => !busy && setConfirm(null)}
          onConfirm={runConfirmed}
          title={confirmCopy.title}
          message={confirmCopy.message}
          confirmText={confirmCopy.confirmText}
          confirmColor={confirmCopy.color}
          loading={!!busy}
        >
          <ConfirmFacts items={[{ label: confirm.exam.classes.length === 1 ? 'Class' : 'Classes', value: confirm.exam.classes.map(c => fmtClassName(c.name)).join(', ') }]} />
        </ConfirmationDialog>
      )}
    </Box>
  );
};

export default ManageExamsPage;
